/**
 * Prospect Search Schemas
 *
 * Zod validation schemas for prospect search and import functionality.
 * Issue #360: Juicebox-Style Pipeline Generation
 *
 * Integrates with SFR Analytics for investor/property data.
 */

import { z } from 'zod';

// ============================================================================
// ENUMS (Domain-specific to avoid collision with shared enums)
// ============================================================================

/** Search type for prospect searches (manual, AI-generated, or template) */
export const ProspectSearchTypeSchema = z.enum(['manual', 'ai_generated', 'template']);
export type ProspectSearchType = z.infer<typeof ProspectSearchTypeSchema>;

export const DataSourceSchema = z.enum(['sfr_analytics', 'internal', 'combined']);
export type DataSource = z.infer<typeof DataSourceSchema>;

export const ScheduleFrequencySchema = z.enum(['daily', 'weekly', 'monthly']);
export type ScheduleFrequency = z.infer<typeof ScheduleFrequencySchema>;

/** Import job status for prospect imports */
export const ProspectImportJobStatusSchema = z.enum([
  'pending',
  'searching',
  'importing',
  'completed',
  'failed',
  'cancelled',
]);
export type ProspectImportJobStatus = z.infer<typeof ProspectImportJobStatusSchema>;

// ============================================================================
// FILTER CRITERIA SCHEMAS
// ============================================================================

/**
 * Investor filter criteria matching SFR Analytics buyer search
 *
 * Updated to support full SFR Analytics OpenAPI spec parameters.
 * @see https://api.sfranalytics.com/openapi.json
 */
export const InvestorFilterCriteriaSchema = z.object({
  // ============================================================================
  // GEOGRAPHIC CRITERIA
  // ============================================================================
  states: z.array(z.string().length(2)).optional(), // 2-letter state codes
  counties: z.array(z.string()).optional(),
  cities: z.array(z.string()).optional(),
  zipCodes: z.array(z.string().max(10)).optional(), // Maps to "zips" in SFR API
  msas: z.array(z.string()).optional(), // Metropolitan Statistical Areas

  // Geo-radius search (lat/lng with radius)
  latitude: z.number().min(-90).max(90).optional(),
  longitude: z.number().min(-180).max(180).optional(),
  radiusMiles: z.number().min(0).max(100).optional(), // Radius in miles

  // ============================================================================
  // INVESTOR CHARACTERISTICS
  // ============================================================================
  // Our investorTypes (llc, trust, etc.) - for filtering by entity structure
  investorTypes: z.array(z.enum(['individual', 'llc', 'trust', 'corporation', 'partnership'])).optional(),

  // SFR Analytics buyer_type - individual vs corporate (broader classification)
  buyerType: z.enum(['all', 'individual', 'corporate']).optional(),

  // Purchase classification
  purchaseType: z.enum(['all', 'non-discount', 'discounted']).optional(),

  // Portfolio size
  minPropertiesOwned: z.number().int().min(0).optional(),
  maxPropertiesOwned: z.number().int().min(0).optional(),
  minPortfolioValue: z.number().min(0).optional(),
  maxPortfolioValue: z.number().min(0).optional(),

  // ============================================================================
  // LOAN & FINANCING
  // ============================================================================
  // Radar API loan_types param — comma-joined before sending
  loanTypes: z.array(z.string()).optional(), // e.g. ['bridge', 'dscr', 'fix-and-flip']
  // Radar API lender_name param
  lenderName: z.string().optional(), // e.g. 'Bank of America'

  // ============================================================================
  // TRANSACTION FILTERS
  // ============================================================================
  minTransactionCount: z.number().int().min(0).optional(), // buyer_transactions_count_min
  maxTransactionCount: z.number().int().min(0).optional(), // buyer_transactions_count_max
  // Price filters (aligned with OpenAPI sales_price_min/max)
  minSalePrice: z.number().min(0).optional(), // sales_price_min
  maxSalePrice: z.number().min(0).optional(), // sales_price_max
  // @deprecated - use minSalePrice/maxSalePrice instead
  minSaleAmount: z.number().min(0).optional(), // sale_amt_min
  maxSaleAmount: z.number().min(0).optional(), // sale_amt_max

  // ============================================================================
  // DATE RANGE FILTERS
  // ============================================================================
  // Days-based (convenience)
  lastPurchaseDays: z.number().int().min(0).optional(), // Days since last purchase
  lastSaleDays: z.number().int().min(0).optional(), // Days since last sale
  lastActivityDays: z.number().int().min(0).optional(), // Days since any activity

  // Exact date range (aligned with OpenAPI sales_date_min/max)
  salesDateStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(), // YYYY-MM-DD
  salesDateEnd: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(), // YYYY-MM-DD
  // @deprecated - use salesDateStart/salesDateEnd instead
  recordingDateStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(), // YYYY-MM-DD
  recordingDateEnd: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(), // YYYY-MM-DD

  // ============================================================================
  // BUYER PROFILE FILTERS (from /buyer-profiles endpoint)
  // ============================================================================
  minEquityPercentage: z.number().min(0).max(100).optional(),
  maxEquityPercentage: z.number().min(0).max(100).optional(),
  minAvgPropertyValue: z.number().min(0).optional(),
  maxAvgPropertyValue: z.number().min(0).optional(),
  minGrowthPercentage: z.number().optional(), // Can be negative
  maxGrowthPercentage: z.number().optional(),

  // ============================================================================
  // PROPERTY TYPE FOCUS
  // ============================================================================
  propertyTypes: z.array(z.enum(['sfr', 'multifamily', 'commercial', 'land', 'condo'])).optional(),

  // ============================================================================
  // PROPERTY CHARACTERISTICS (aligned with OpenAPI)
  // ============================================================================
  minBedrooms: z.number().int().min(0).optional(), // beds_min
  maxBedrooms: z.number().int().min(0).optional(), // beds_max
  minBathrooms: z.number().min(0).optional(), // baths_min
  maxBathrooms: z.number().min(0).optional(), // baths_max
  minBuildingSqft: z.number().int().min(0).optional(), // building_sqft_min
  maxBuildingSqft: z.number().int().min(0).optional(), // building_sqft_max

  // ============================================================================
  // SCORING & CREDIT
  // ============================================================================
  minInvestorScore: z.number().min(0).max(100).optional(),
  maxInvestorScore: z.number().min(0).max(100).optional(),
  creditIndicators: z.array(z.enum(['excellent', 'good', 'fair', 'poor'])).optional(),

  // ============================================================================
  // TEXT SEARCH
  // ============================================================================
  keywords: z.array(z.string()).optional(),
  companyName: z.string().optional(),
  name: z.string().optional(),

  // ============================================================================
  // SORTING & PAGINATION
  // ============================================================================
  sortBy: z.string().optional(), // Field to sort by (prefix with - for descending)
});
export type InvestorFilterCriteria = z.infer<typeof InvestorFilterCriteriaSchema>;

// ============================================================================
// AI SEARCH SCHEMAS
// ============================================================================

export const AISearchInputSchema = z.object({
  query: z.string().min(3).max(500),
});
export type AISearchInput = z.infer<typeof AISearchInputSchema>;

export const AISearchResultSchema = z.object({
  originalQuery: z.string(),
  filterCriteria: InvestorFilterCriteriaSchema,
  confidence: z.number().min(0).max(1),
  suggestions: z.array(z.string()).optional(), // Alternative query suggestions
  usedFallback: z.boolean().optional(), // True if local parsing was used instead of LLM
});
export type AISearchResult = z.infer<typeof AISearchResultSchema>;

// ============================================================================
// PROSPECT SEARCH SCHEMAS
// ============================================================================

export const CreateProspectSearchSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  naturalLanguageQuery: z.string().max(500).optional(),
  filterCriteria: InvestorFilterCriteriaSchema,
  searchType: ProspectSearchTypeSchema.default('manual'),
  dataSource: DataSourceSchema.default('sfr_analytics'),

  // Scheduling
  isScheduled: z.boolean().default(false),
  scheduleFrequency: ScheduleFrequencySchema.optional(),

  // Auto-import settings
  autoImport: z.boolean().default(false),
  importLimit: z.number().int().min(1).max(500).default(100),
  assignTo: z.string().uuid().optional(),
  applyTags: z.array(z.string()).default([]),
  enrollInSequence: z.string().uuid().optional(),
});
export type CreateProspectSearchInput = z.infer<typeof CreateProspectSearchSchema>;

export const UpdateProspectSearchSchema = z.object({
  searchId: z.string().uuid(),
  data: CreateProspectSearchSchema.partial(),
});
export type UpdateProspectSearchInput = z.infer<typeof UpdateProspectSearchSchema>;

export const ProspectSearchSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  naturalLanguageQuery: z.string().nullable(),
  filterCriteria: InvestorFilterCriteriaSchema,
  searchType: ProspectSearchTypeSchema,
  dataSource: DataSourceSchema,
  lastExecutedAt: z.string().datetime().nullable(),
  lastResultCount: z.number().int().nullable(),
  isScheduled: z.boolean(),
  scheduleFrequency: ScheduleFrequencySchema.nullable(),
  nextScheduledAt: z.string().datetime().nullable(),
  autoImport: z.boolean(),
  importLimit: z.number().int(),
  assignTo: z.string().uuid().nullable(),
  applyTags: z.array(z.string()),
  enrollInSequence: z.string().uuid().nullable(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ProspectSearch = z.infer<typeof ProspectSearchSchema>;

// ============================================================================
// SEARCH EXECUTION SCHEMAS
// ============================================================================

export const ExecuteSearchSchema = z.object({
  filterCriteria: InvestorFilterCriteriaSchema,
  limit: z.number().int().min(1).max(100).default(25),
  offset: z.number().int().min(0).default(0),
});
export type ExecuteSearchInput = z.infer<typeof ExecuteSearchSchema>;

export const SearchResultProspectSchema = z.object({
  id: z.string(),
  fullName: z.string().nullable(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  company: z.string().nullable(),
  investorType: z.string().nullable(),
  totalPropertiesOwned: z.number().int().nullable(),
  portfolioValue: z.number().nullable(),
  investorScore: z.number().min(0).max(100).nullable(),
  creditIndicator: z.string().nullable(),
  lastPurchaseDate: z.string().datetime().nullable(),
  lastSaleDate: z.string().datetime().nullable(),
  currentAddress: z
    .object({
      street: z.string().nullable(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zipCode: z.string().nullable(),
    })
    .nullable(),
  dataQualityScore: z.number().min(0).max(100).nullable(),
  // Flag if already exists as lead
  existsAsLead: z.boolean().default(false),
  existingLeadId: z.string().uuid().nullable(),
});
export type SearchResultProspect = z.infer<typeof SearchResultProspectSchema>;

export const SearchResultSchema = z.object({
  prospects: z.array(SearchResultProspectSchema),
  totalCount: z.number().int(),
  hasMore: z.boolean(),
  searchId: z.string().optional(), // For pagination
});
export type SearchResult = z.infer<typeof SearchResultSchema>;

// ============================================================================
// IMPORT JOB SCHEMAS
// ============================================================================

export const StartImportJobSchema = z.object({
  searchId: z.string().uuid().optional(), // From saved search
  filterCriteria: InvestorFilterCriteriaSchema.optional(), // Or direct criteria
  limit: z.number().int().min(1).max(500).default(100),
  assignTo: z.string().uuid().optional(),
  applyTags: z.array(z.string()).default([]),
  enrollInSequence: z.string().uuid().optional(),
});
export type StartImportJobInput = z.infer<typeof StartImportJobSchema>;

export const ImportJobSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  searchId: z.string().uuid().nullable(),
  status: ProspectImportJobStatusSchema,
  totalFound: z.number().int(),
  imported: z.number().int(),
  duplicates: z.number().int(),
  failed: z.number().int(),
  filterCriteria: InvestorFilterCriteriaSchema,
  importedLeadIds: z.array(z.string().uuid()),
  errors: z.array(z.string()).nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
});
export type ImportJob = z.infer<typeof ImportJobSchema>;

// ============================================================================
// LIST SCHEMAS
// ============================================================================

export const ListProspectSearchesSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  search: z.string().optional(),
});
export type ListProspectSearchesInput = z.infer<typeof ListProspectSearchesSchema>;

export const ListImportJobsSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
  status: ProspectImportJobStatusSchema.optional(),
  searchId: z.string().uuid().optional(),
});
export type ListImportJobsInput = z.infer<typeof ListImportJobsSchema>;

// ============================================================================
// LEAD ACTIVITY SCHEMAS
// ============================================================================

export const LeadActivityTypeSchema = z.enum([
  'viewed',
  'status_changed',
  'qualification_changed',
  'score_changed',
  'email_sent',
  'email_opened',
  'email_clicked',
  'email_replied',
  'sequence_enrolled',
  'sequence_step_sent',
  'sequence_completed',
  'sequence_exited',
  'enriched',
  'note_added',
  'call_made',
  'call_received',
  'meeting_scheduled',
  'shortlisted',
  'tagged',
  'assigned',
  'imported',
]);
export type LeadActivityType = z.infer<typeof LeadActivityTypeSchema>;

export const PerformerTypeSchema = z.enum(['user', 'system', 'lead', 'agent', 'webhook']);
export type PerformerType = z.infer<typeof PerformerTypeSchema>;

export const CreateLeadActivitySchema = z.object({
  leadId: z.string().uuid(),
  activityType: LeadActivityTypeSchema,
  title: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  sequenceId: z.string().uuid().optional(),
  campaignId: z.string().uuid().optional(),
  emailId: z.string().optional(),
  callId: z.string().uuid().optional(),
});
export type CreateLeadActivityInput = z.infer<typeof CreateLeadActivitySchema>;

export const LeadActivitySchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  leadId: z.string().uuid(),
  activityType: LeadActivityTypeSchema,
  performedBy: z.string().nullable(),
  performedByName: z.string().nullable(),
  performedByType: PerformerTypeSchema,
  title: z.string(),
  description: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  sequenceId: z.string().uuid().nullable(),
  campaignId: z.string().uuid().nullable(),
  emailId: z.string().nullable(),
  callId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
});
export type LeadActivity = z.infer<typeof LeadActivitySchema>;

export const ListLeadActivitiesSchema = z.object({
  leadId: z.string().uuid(),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(50),
  activityTypes: z.array(LeadActivityTypeSchema).optional(),
});
export type ListLeadActivitiesInput = z.infer<typeof ListLeadActivitiesSchema>;

// ============================================================================
// LEAD HIGHLIGHT SCHEMAS
// ============================================================================

export const HighlightTypeSchema = z.enum([
  'likely_to_switch',
  'high_value_investor',
  'portfolio_owner',
  'repeat_buyer',
  'distressed_asset_buyer',
  'market_timing',
  'recent_activity',
  'geographic_focus',
  'property_specialist',
  'investor_network',
  'custom',
]);
export type HighlightType = z.infer<typeof HighlightTypeSchema>;

export const LeadHighlightSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  leadId: z.string().uuid(),
  highlightType: HighlightTypeSchema,
  label: z.string(),
  description: z.string().nullable(),
  score: z.number().int().min(0).max(100).nullable(),
  icon: z.string().nullable(),
  color: z.string().nullable(),
  generatedBy: z.string(),
  modelVersion: z.string().nullable(),
  validUntil: z.string().datetime().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type LeadHighlight = z.infer<typeof LeadHighlightSchema>;

// ============================================================================
// SHORTLIST SCHEMAS
// ============================================================================

export const CreateShortlistSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional(),
  leadIds: z.array(z.string().uuid()).optional(),
});
export type CreateShortlistInput = z.infer<typeof CreateShortlistSchema>;

export const AddToShortlistSchema = z.object({
  shortlistId: z.string().uuid(),
  leadIds: z.array(z.string().uuid()).min(1).max(100),
  notes: z.string().max(1000).optional(),
});
export type AddToShortlistInput = z.infer<typeof AddToShortlistSchema>;

export const ShortlistSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  leadCount: z.number().int(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Shortlist = z.infer<typeof ShortlistSchema>;
